import React, { useEffect, useMemo, useState } from 'react';
import './DuplicateBill.scss';
import {
  Grid,
  Box,
  Button,
  FormControlLabel,
  FormControl,
  RadioGroup,
  Radio,
} from '@mui/material';
import TextField from '../../../components/SanitizedTextField/SanitizedTextField';
import Backdrop from '@mui/material/Backdrop';
import AppLoader from '../../../components/AppLoader/AppLoader';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Error from '../../../components/error/error';
import { dBillAPI, downloadBill } from '../../../services/Functions';
import { useSelector } from 'react-redux';
import { resolveDiscoCompanyCodeForBranding } from '../../../utils/adminCompany';
import {
    applyCustomerIdLengthValidation,
    getCustomerIdInputMaxLength,
    isValidCustomerIdLength,
} from '../../../utils/customerIdValidation';

const DuplicateBill = () => {
    const initialValues = {
        iv_reference_number: '',
        iv_consumer_id: '',
    };

    const [viewBackdrop, setViewBackdrop] = useState(false);
    const [searchBy, setSearchBy] = useState('consumerId');
    const [referenceNumber, setReferenceNumber] = useState('');
    const [error, setError] = useState('');
    const { token, code: userCode, roleId, selectedAdminCompanyCode } = useSelector((state) => state.auth);
    const userCompany = resolveDiscoCompanyCodeForBranding(
        userCode,
        roleId,
        selectedAdminCompanyCode,
    );
    const customerIdInputMaxLength = getCustomerIdInputMaxLength(userCompany);

    const validationSchema = useMemo(
        () =>
            Yup.object({
                iv_reference_number: Yup.string().when('iv_search_by', {
                    is: 'referenceNo',
                    then: () =>
                        Yup.string()
                            .transform((value) => (value ? String(value).replace(/\D/g, '') : ''))
                            .matches(/^\d+$/, 'Reference No. must contain digits only.')
                            .min(14, 'Reference No. must be 14 digits.')
                            .max(14, 'Reference No. must be 14 digits.')
                            .required('Enter reference number.'),
                    otherwise: () => Yup.string().optional(),
                }),
                iv_consumer_id: Yup.string().when('iv_search_by', {
                    is: 'consumerId',
                    then: () =>
                        applyCustomerIdLengthValidation(
                            Yup.string()
                            .transform((value) => (value ? String(value).replace(/\D/g, '') : ''))
                            .matches(/^\d+$/, 'Customer ID must contain digits only.'),
                            userCompany,
                            'short',
                        ).required('Enter customer ID.'),
                    otherwise: () => Yup.string().optional(),
                }),
                iv_search_by: Yup.string().required(),
            }),
        [userCompany]
    );

    const { values, errors, touched, handleBlur, handleSubmit, setFieldValue } = useFormik({
        initialValues: {
            ...initialValues,
            iv_search_by: 'consumerId',
        },
        validationSchema,
        onSubmit: async () => {
            setViewBackdrop(true);
            try {
                if (searchBy === 'consumerId') {
                    await downloadBill(referenceNumber);
                } else {
                    await downloadBill(values.iv_reference_number);
                }
            } finally {
                setViewBackdrop(false);
            }
        },
    });

    const SearchBy = (event) => {
        const next = event.target.value;
        setSearchBy(next);
        setFieldValue('iv_search_by', next);
        setFieldValue('iv_consumer_id', '');
        setFieldValue('iv_reference_number', '');
        setError('');
        setReferenceNumber('');
    };

    const handleNumericChange = (event, fieldName, maxLength) => {
        const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, maxLength);
        setFieldValue(fieldName, digitsOnly);
    };

    const referenceApi = async () => {
        if (searchBy !== 'consumerId' || !values.iv_consumer_id) return;

        setViewBackdrop(true);
        const apiResponse = await dBillAPI(token, values.iv_consumer_id);
        if (apiResponse.status) {
            const basicInfo = apiResponse.basicInfo;
            setReferenceNumber(basicInfo.refNo.replace(/ /g, '').slice(0, 14));
            setError('');
        } else if (apiResponse.status === false) {
            setReferenceNumber('');
            setError('No data found against this Customer ID.');
        } else {
            setReferenceNumber('');
            setError('Customer ID not found.');
        }
        setViewBackdrop(false);
    };

    useEffect(() => {
        if (searchBy !== 'consumerId') return;

        if (isValidCustomerIdLength(values.iv_consumer_id, userCompany)) {
            referenceApi();
        } else {
            setReferenceNumber('');
            setError('');
        }
    }, [values.iv_consumer_id, searchBy]);

    const errorCloses = () => {
        setError('');
    };

    const submitEnabled =
        searchBy === 'consumerId'
            ? isValidCustomerIdLength(values.iv_consumer_id, userCompany) &&
              String(referenceNumber || '').length === 14
            : String(values.iv_reference_number || '').length === 14;

    const handleFormKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (submitEnabled) {
                handleSubmit();
            }
        }
    };

    return (
        <>
            <Grid container className="duplicate-bill-page" sx={{ width: '100%', maxWidth: '100%' }}>
                <Box className="duplicate-bill-container" sx={{ width: '100%', maxWidth: '100%' }}>
                    <h3 className="heading">Duplicate Bill</h3>
                    <form className="db-form" onKeyDown={handleFormKeyDown}>
                        <div className="section-card section-card--accent">
                            <div className="section-title">Connection Details</div>
                            <p className="section-subtitle">
                                Search by Customer ID or Reference No., then download the duplicate bill PDF.
                            </p>

                            <div className="db-search-fields">
                                <div className="field-wrap field-wrap--search-by db-search-fields__item">
                                    <label className="fieldLabel">Search By</label>
                                    <div className="db-search-fields__control">
                                        <FormControl
                                            margin="none"
                                            className="form-control form-control--search-by"
                                        >
                                            <RadioGroup
                                                className="radio-group"
                                                row
                                                name="iv_search_by"
                                                value={searchBy}
                                                onChange={SearchBy}
                                            >
                                                <FormControlLabel
                                                    value="consumerId"
                                                    className="form-control-label"
                                                    control={<Radio size="small" />}
                                                    label="Customer ID"
                                                    labelPlacement="end"
                                                />
                                                <FormControlLabel
                                                    value="referenceNo"
                                                    className="form-control-label"
                                                    control={<Radio size="small" />}
                                                    label="Reference No."
                                                    labelPlacement="end"
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    </div>
                                </div>

                                {searchBy === 'consumerId' && (
                                    <div className="field-wrap db-search-fields__item db-search-fields__item--input">
                                        <label className="fieldLabel" htmlFor="iv_consumer_id">
                                            Customer ID
                                        </label>
                                        <div className="db-search-fields__control">
                                            <TextField
                                                margin="none"
                                                inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                                                required
                                                type="text"
                                                autoComplete="off"
                                                name="iv_consumer_id"
                                                value={values.iv_consumer_id}
                                                onChange={(event) =>
                                                    handleNumericChange(event, 'iv_consumer_id', customerIdInputMaxLength)
                                                }
                                                onBlur={handleBlur}
                                                id="iv_consumer_id"
                                                className="textInputField"
                                                placeholder="10-digit customer ID"
                                                fullWidth
                                            />
                                        </div>
                                        <Box className="from-Error">
                                            {errors.iv_consumer_id && touched.iv_consumer_id
                                                ? errors.iv_consumer_id
                                                : null}
                                        </Box>
                                    </div>
                                )}

                                {searchBy === 'referenceNo' && (
                                    <div className="field-wrap db-search-fields__item db-search-fields__item--input">
                                        <label className="fieldLabel" htmlFor="iv_reference_number">
                                            Reference No.
                                        </label>
                                        <div className="db-search-fields__control">
                                            <TextField
                                                margin="none"
                                                inputProps={{ maxLength: 14, inputMode: 'numeric' }}
                                                required
                                                type="text"
                                                autoComplete="off"
                                                name="iv_reference_number"
                                                value={values.iv_reference_number}
                                                onChange={(event) =>
                                                    handleNumericChange(event, 'iv_reference_number', 14)
                                                }
                                                onBlur={handleBlur}
                                                id="iv_reference_number"
                                                className="textInputField"
                                                placeholder="14-digit reference number"
                                                fullWidth
                                            />
                                        </div>
                                        <Box className="from-Error">
                                            {errors.iv_reference_number && touched.iv_reference_number
                                                ? errors.iv_reference_number
                                                : null}
                                        </Box>
                                    </div>
                                )}
                            </div>

                            {error && (
                                <div className="db-alert-wrap">
                                    <Error
                                        error={error}
                                        onClose={errorCloses}
                                        backgroundColor="#FFC0CB"
                                        textColor="#bb0303"
                                        iconColor="#bb0303"
                                    />
                                </div>
                            )}
                        </div>

                        <Box className="submit-area">
                            <Button
                                className={`submit-btn${!submitEnabled ? ' is-disabled' : ''}`}
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={!submitEnabled}
                            >
                                Submit
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Grid>

            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }}
                open={viewBackdrop}
            >
                <AppLoader />
            </Backdrop>
        </>
    );
};

export default DuplicateBill;
